import React, { useCallback } from "react";
import TimePicker from "./TimePicker";
import { HourMinuteOption, padTwoDigitTime, Period, PERIODS } from "./utils";

export interface TimePickerFromScheduleTimeProps {
  scheduleTime: string;
  onChange: (newTime: string) => void;
  id?: string;
  className?: string;
}

/**
 * Time picker that reads and emits `"HHmm"` format strings (e.g. `"0830"`,
 * `"1330"`). This compact format is used by the SectionedForm schedule fields
 * and {@link SimplifiedSchedulePicker}.
 *
 * The `scheduleTime` string is parsed into 12-hour parts and any changes are
 * re-encoded back to the `"HHmm"` format.
 */
export const TimePickerFromScheduleTime = ({
  scheduleTime,
  onChange,
  id,
  className,
}: TimePickerFromScheduleTimeProps): JSX.Element => {
  const currentHourBase = Number(scheduleTime.substring(0, 2));
  const currentHour = currentHourBase % 12;
  const currentMinute = Number(scheduleTime.substring(2, 4));
  const currentPeriod: Period = currentHourBase < 12 ? PERIODS.AM : PERIODS.PM;

  const handleHoursChange = useCallback(
    ({ value: hour }: HourMinuteOption) => {
      if (currentPeriod === PERIODS.AM) {
        onChange(`${padTwoDigitTime(hour)}${padTwoDigitTime(currentMinute)}`);
      } else {
        onChange(`${hour + 12}${padTwoDigitTime(currentMinute)}`);
      }
    },
    [onChange, currentMinute, currentPeriod]
  );

  const handleMinutesChange = useCallback(
    ({ value: minute }: HourMinuteOption) => {
      if (currentPeriod === PERIODS.AM) {
        onChange(`${padTwoDigitTime(currentHour)}${padTwoDigitTime(minute)}`);
      } else {
        onChange(`${currentHour + 12}${padTwoDigitTime(minute)}`);
      }
    },
    [onChange, currentHour, currentPeriod]
  );

  const handleTimePeriodChange = useCallback(
    (period: Period) => {
      if (period === PERIODS.AM) {
        onChange(
          `${padTwoDigitTime(currentHour)}${padTwoDigitTime(currentMinute)}`
        );
      } else {
        onChange(`${currentHour + 12}${padTwoDigitTime(currentMinute)}`);
      }
    },
    [onChange, currentHour, currentMinute]
  );

  return (
    <TimePicker
      id={id ?? "schedule-time"}
      hours={currentHour}
      minutes={currentMinute}
      period={currentPeriod}
      onHoursChange={handleHoursChange}
      onMinutesChange={handleMinutesChange}
      onTimePeriodChange={handleTimePeriodChange}
      className={className}
    />
  );
};

export default TimePickerFromScheduleTime;
